// CfoInsightsUpgrade.tsx
// Upgrade/Upsell page for CFO Insights premium feature

import React from "react";
import { Link } from "react-router-dom";

export function CfoInsightsUpgrade() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/30">
              <span className="text-lg font-bold text-sky-300">CP</span>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sky-400">
                ConstructPay
              </div>
              <div className="text-sm font-semibold text-slate-100">
                CFO-Insights
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-12">
        <div className="mx-auto max-w-2xl text-center">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-sky-500/10 border border-sky-500/30">
              <svg
                className="h-10 w-10 text-sky-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1 className="mb-4 text-3xl font-bold text-slate-50">
            CFO-Insights Premium Feature
          </h1>

          {/* Description */}
          <p className="mb-8 text-lg text-slate-300">
            CFO-Insights is a premium feature that provides comprehensive financial
            analytics, AR/AP aging, cash flow projections, and capital stack
            recommendations for trade partners.
          </p>

          {/* Features List */}
          <div className="mb-8 rounded-xl border border-slate-800 bg-slate-900/70 p-6 text-left">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-slate-400">
              What You'll Get
            </h2>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">Executive Summary:</strong>{" "}
                  High-level financial health indicators and ConstructScore
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">Cash Flow Analysis:</strong>{" "}
                  30-day cash projections and at-risk invoice tracking
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">Margin & COS:</strong>{" "}
                  Detailed cost breakdown and overhead analysis
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">AR/AP Risk:</strong>{" "}
                  Receivables and payables visibility with aging risk
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">Bankability Metrics:</strong>{" "}
                  Key ratios for lenders, sureties, and GC risk review
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">CapitalStack:</strong>{" "}
                  Aligns your numbers with what GCs, banks, and sureties care about so you look 'fundable' without sharing your full financials
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 text-sky-400">✓</span>
                <span>
                  <strong className="text-slate-50">Aging Detail:</strong>{" "}
                  Invoice-level aging with customer rollups and risk tiers
                </span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mb-8 rounded-xl border border-slate-800 bg-slate-900/70 p-6">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-slate-400">
              How to Enable
            </h2>
            <p className="mb-4 text-sm text-slate-300">
              CFO-Insights is a premium feature. To enable it for your organization,
              please contact:
            </p>
            <div className="space-y-2 text-sm text-slate-200">
              <p>
                • <strong>Your General Contractor</strong> (if enrolled in ConstructPay)
              </p>
              <p>
                • <strong>Your Lender</strong> (if using ConstructPay financing)
              </p>
              <p>
                • <strong>ConstructPay Support</strong> at{" "}
                <a
                  href="mailto:support@constructpay.com"
                  className="text-sky-400 hover:text-sky-300 underline"
                >
                  support@constructpay.com
                </a>
              </p>
            </div>
          </div>

          {/* Back Link */}
          <div className="flex justify-center gap-4">
            <Link
              to="/"
              className="rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-2 text-sm text-slate-300 transition hover:bg-slate-800 hover:text-slate-50"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

